import React from 'react';
import { IScheduledJob, ScheduledJobType, ScheduleInterval } from '@skystone/shared';
import { scheduledJobApi } from '@/services/scheduled-job-api';
import { formatDateTime } from '@/lib/date-utils';
import { DataTable, ColumnDef } from '@/components/ui/DataTable';
import { Button, Badge, TrashIcon, PageHeader, PageLoading, ErrorBanner } from '@/components/ui';
import { formatScheduleLabel, getUserTimezone } from '@/lib/schedule-utils';
import { useApi } from '@/hooks/useApi';
import { getErrorMessage } from '@/lib/utils';
import { coerceScalarToString } from '@/lib/table-cell-renderers';
import { PageContainer } from '@/components/layout';

const JOB_TYPE_LABELS: Partial<Record<ScheduledJobType, string>> = {
  DATA_QUALITY_CHECK: 'Data Quality Check',
  REPORT_GENERATION: 'Report Generation',
  DATA_SYNC: 'Data Sync',
  SLACK_MEMBER_SYNC: 'Slack Member Sync',
  FABRIC_PRODUCT_SYNC: 'Fabric Product Sync',
  FABRIC_TEAM_SYNC: 'Fabric Team Sync',
  FABRIC_REPOSITORY_SYNC: 'Fabric Repository Sync',
  FABRIC_INCIDENT_SYNC: 'Fabric Incident Sync',
  FABRIC_ISSUE_CURRENT_SYNC: 'Fabric Issue Current Sync',
  FABRIC_DEPLOYMENT_SYNC: 'Fabric Deployment Sync',
  FABRIC_PULL_REQUEST_SYNC: 'Fabric Pull Request Sync',
  FABRIC_DEVEX_QUARTERLY_SYNC: 'Fabric DevEx Quarterly Sync',
  FABRIC_CURATED_REBUILD_SYNC: 'Fabric Curated Rebuild Sync',

  FABRIC_SONARQUBE_SYNC: 'Fabric Sonarqube Sync',
  FABRIC_PIPELINE_STATUS_SYNC: 'Fabric Pipeline Status Sync',
  TABLE_RELATIONSHIP_OVERVIEW: 'Table Relationship Overview',
};

// ── Module-level column factory ───────────────────────────────────────────────

function createJobColumns(
  onToggle: (job: IScheduledJob) => Promise<void>,
  onTrigger: (id: string, e: React.MouseEvent) => Promise<void>,
  onDelete: (id: string, e: React.MouseEvent) => Promise<void>,
): ColumnDef<IScheduledJob>[] {
  return [
    {
      id: 'name',
      header: 'Name',
      accessor: 'name',
      sortable: true,
      filter: { type: 'text', placeholder: 'Filter name...' },
    },
    {
      id: 'type',
      header: 'Type',
      accessor: 'type',
      cell: (value) => {
        const jobType = value as ScheduledJobType;
        const label = JOB_TYPE_LABELS[jobType] ?? jobType;
        return <Badge variant="secondary">{label}</Badge>;
      },
    },
    {
      id: 'scheduleInterval',
      header: 'Schedule',
      accessor: 'scheduleInterval',
      cell: (value) => formatScheduleLabel(value as ScheduleInterval),
    },
    {
      id: 'enabled',
      header: 'Status',
      accessor: 'enabled',
      cell: (value) => (
        <Badge variant={value ? 'default' : 'secondary'}>
          {value ? 'Enabled' : 'Disabled'}
        </Badge>
      ),
    },
    {
      id: 'lastRunAt',
      header: 'Last Run',
      accessor: 'lastRunAt',
      sortable: true,
      cell: (value) => formatDateTime(value as string | null),
    },
    {
      id: 'lastRunStatus',
      header: 'Last Status',
      accessor: 'lastRunStatus',
      cell: (value) => {
        if (!value) return <span className="text-muted-foreground">{'\u2014'}</span>;
        return (
          <Badge variant={value === 'success' ? 'success' : 'error'}>
            {coerceScalarToString(value)}
          </Badge>
        );
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      accessor: 'id',
      cell: (_value, row) => (
        <div className="flex gap-2 justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              void onToggle(row);
            }}
          >
            {row.enabled ? 'Disable' : 'Enable'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => onTrigger(row.id, e)}
          >
            Trigger
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => onDelete(row.id, e)}
            className="text-muted-foreground hover:text-error hover:bg-error/10"
            title="Delete Job"
          >
            <TrashIcon className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];
}

// ── Component ─────────────────────────────────────────────────────────────────

export function ScheduledJobsPage() {
  const { data: jobs, loading, error, refetch } = useApi(() => scheduledJobApi.getAll(), []);

  const handleToggleEnabled = async (job: IScheduledJob): Promise<void> => {
    try {
      if (job.enabled) {
        await scheduledJobApi.disable(job.id);
      } else {
        await scheduledJobApi.enable(job.id);
      }
      refetch();
    } catch (err: unknown) {
      alert(getErrorMessage(err, 'Failed to toggle job'));
    }
  };

  const handleDelete = async (id: string, e: React.MouseEvent): Promise<void> => {
    e.stopPropagation();
    if (!confirm('Are you sure you want to delete this scheduled job?')) return;
    try {
      await scheduledJobApi.delete(id);
      refetch();
    } catch (err: unknown) {
      alert(getErrorMessage(err, 'Failed to delete job'));
    }
  };

  const handleTrigger = async (id: string, e: React.MouseEvent): Promise<void> => {
    e.stopPropagation();
    try {
      await scheduledJobApi.trigger(id);
      alert('Job triggered successfully');
      refetch();
    } catch (err: unknown) {
      alert(getErrorMessage(err, 'Failed to trigger job'));
    }
  };

  if (loading) return <PageLoading />;

  const allJobs = jobs ?? [];
  const columns = createJobColumns(handleToggleEnabled, handleTrigger, handleDelete);

  return (
    <PageContainer variant="wide" className="space-y-6">
      <PageHeader
        title="Scheduled Jobs"
        subtitle="Manage recurring background tasks."
      />

      <ErrorBanner message={error} />

      <p className="text-xs text-muted-foreground">
        Schedule times are shown in your local timezone ({getUserTimezone()}).
      </p>

      <DataTable
        data={allJobs}
        columns={columns}
        keyExtractor={(job) => job.id}
        enablePagination
        enableFilters
        emptyMessage="No scheduled jobs found."
      />
    </PageContainer>
  );
}
